import React, { Component } from "react"
import Helmet from "react-helmet"
import Link from "gatsby-link"
import PropTypes from "prop-types"
import "../css/index.css"
import "../css/typography.css"
import SectionOneBackground from "../assets/pngs/SectionOneBackground.png"
import SectionTwoBackground from "../assets/pngs/SectionTwoBackground.png"
import SectionThreeBackground from "../assets/pngs/SectionThreeBackground.png"
import SectionFourBackground from "../assets/pngs/SectionFourBackground.png"
import SectionFiveBackground from "../assets/pngs/SectionFiveBackground.png"
import SectionCalloutBackground from "../assets/pngs/SectionCalloutBackground.png"
import slackMeetSmallchat from "../assets/svgs/slackMeetSmallchat.svg"
import customerChat from "../assets/svgs/customerChat.svg"
import HeaderConversation from "../assets/svgs/HeaderConversation.svg"

import SectionCallout from "../components/SectionCallout"
import ArticleCard from "../components/ArticleCard"
import SectionCenteredContent from "../components/SectionCenteredContent"
import HomeFeatureSet from "../components/HomeFeatureSet"
import SectionBlockContent from "../components/SectionBlockContent"
import SectionIllustration from "../components/SectionIllustration"
import SectionHeaderContent from "../components/SectionHeaderContent"
import SectionBackground from "../components/SectionBackground"
import SectionContainer from "../components/SectionContainer"
import withScreensize from "../components/withScreensize"
import ScreensizeProvider from "../components/ScreensizeProvider"

const pageData = {
  headline: "Talk to your visitors without leaving Slack.",
  copy:
    "Start conversations with visitors on your website through Smallchat and convert those visitors into customers. All from inside Slack.",

  slackHeadline: "Smallchat + Slack.",
  slackCopy:
    "Smallchat runs inside your Slack team, which means no additional software to learn. Be available to chat with visitors on your website and your team members all in one place. Each conversation creates a new thread, allowing your team to manage it all from one place.",
  slackProTip:
    "Pro Tip: With Smallchat Pro, you can add additional sites in their own channel.",

  customerHeadline: "Customer chat simplified.",
  customerCopy:
    "With Smallchat there’s no need to add another piece of software to your workflow. Bring all your communication together in Slack.",
  customerProTip: "",

  statTitle:
    "Add your voice to the 50,000 conversations happening on Smallchat.",
  statDescription:
    "Completely customizable for any brand, any platform & any language.",

  homeFeatureTitle: "All the features you expect.",

  HomeFeatureSet: [
    {
      iconName: "contactList",
      featureName: "Contact List",
      featureDescription:
        "Quickly and easily view your customer information or export a CSV to your favorite email platform."
    },
    {
      iconName: "easyInstall",
      featureName: "Easily Install",
      featureDescription:
        "Add a single embed script to your site. Tweak some settings, and your widget updates automatically.",
      featureLink: "https://stories.nicer.io/smallchat-on-shopify-44925d05d73e",
      featureLinkText: "Using Shopify?"
    },
    {
      iconName: "customizable",
      featureName: "Fully Customizable",
      featureDescription:
        "Customize Smallchat with easy styling that lets you seamlessly integrate with your brand.",
      featureLink: "https://docs.small.chat/configuration",
      featureLinkText: "More Customization Tips"
    },
    {
      iconName: "autoMessages",
      featureName: "Auto Messages",
      featureDescription:
        "Get the conversation started! Actively engage visitors with custom trigger messages.",
      featureLink: "https://docs.small.chat/automessages",
      featureLinkText: "Read More"
    },
    {
      iconName: "editMessages",
      featureName: "Instant Message Editing",
      featureDescription:
        "No need to fret over typos. Edit or delete messages even after they've been sent.",
      featureLink:
        "https://stories.nicer.io/slacks-tips-we-use-at-smallchat-ad6c47a50db0",
      featureLinkText: "Learn More Slack Tips"
    },
    {
      iconName: "analytics",
      featureName: "Conversation Analytics",
      featureDescription:
        "Track the number of interactions you're getting month to month. To see how your business is performing."
    }
  ],

  ArticleSet: [
    {
      iconName: "autoMessages",
      articleName: "Why You Should Use Auto Messages",
      articleDescription:
        "Get conversations started! Engage visitors with custom trigger messages.",
      articleLink: "https://docs.small.chat/automessages",
      articleLinkText: "Read More"
    },
    {
      iconName: "slack",
      articleName: "Slack Tips We Use at Smallchat",
      articleDescription:
        "Get the most out of your Slack + Smallchat experience with these helpful tips.",
      articleLink:
        "https://stories.nicer.io/slacks-tips-we-use-at-smallchat-ad6c47a50db0",
      articleLinkText: "Read Article"
    }
  ]
}

function Home({ screensize }) {
  const isMobile = screensize === "phone"
  const isTablet = screensize === "tablet"

  return (
    <div>
      <Helmet title="Smallchat — Connect with your visitors." />
      <SectionContainer height={isMobile ? "90vh" : isTablet ? "90vh" : "80vh"}>
        <SectionHeaderContent
          name={HeaderConversation}
          headline={pageData.headline}
          copy={pageData.copy}
        />
        <SectionBackground
          background={SectionOneBackground}
          backgroundWidth={isMobile ? "120%" : isTablet ? "110%" : "100%"}
          backgroundHeight="100%"
          backgroundRight="35%"
          backgroundScale={isMobile ? "1.5" : isTablet ? "1.2" : "1"}
        />
      </SectionContainer>
      <SectionContainer height={isMobile ? "90vh" : isTablet ? "70vh" : "80vh"}>
        <SectionBlockContent
          headline={pageData.slackHeadline}
          copy={pageData.slackCopy}
          protips={pageData.slackProTip}
        />
        <SectionIllustration
          transform={
            isMobile ? "scale(.6)" : isTablet ? "scale(.8)" : "scale(1)"
          }
          position={isMobile ? "-40%" : isTablet ? "0%" : "0%"}
          name={slackMeetSmallchat}
        />
        <SectionBackground
          background={SectionTwoBackground}
          backgroundWidth={isMobile ? "180%" : isTablet ? "140%" : "100%"}
          backgroundHeight="100%"
          backgroundRight="-5%"
          backgroundScale={isMobile ? "1.6" : isTablet ? "1.4" : "1.2"}
        />
      </SectionContainer>
      <SectionContainer
        height={isMobile ? "120vh" : isTablet ? "95vh" : "75vh"}>
        <HomeFeatureSet
          title={pageData.homeFeatureTitle}
          features={pageData.HomeFeatureSet}
        />
        <SectionBackground
          background={SectionThreeBackground}
          backgroundWidth="100%"
          backgroundHeight={isMobile ? "40%" : isTablet ? "60%" : "100%"}
          backgroundRight="35%"
          backgroundScale={isMobile ? "1.5" : isTablet ? "1.2" : "1"}
        />
      </SectionContainer>
      <SectionContainer height={isMobile ? "50vh" : isTablet ? "50vh" : "50vh"}>
        <SectionBlockContent
          headline={pageData.customerHeadline}
          copy={pageData.customerCopy}
          protips={pageData.customerProTip}
        />
        <SectionIllustration
          zIndex="10"
          name={customerChat}
          transform={
            isMobile ? "scale(.5)" : isTablet ? "scale(.7)" : "scale(.9)"
          }
          position={isMobile ? "-12%" : isTablet ? "10%" : "-5%"}
        />
        <SectionBackground
          background={SectionFourBackground}
          backgroundWidth={isMobile ? "150%" : isTablet ? "90%" : "100%"}
          backgroundHeight={isMobile ? "70%" : isTablet ? "90%" : "100%"}
          backgroundRight={isMobile ? "-40%" : isTablet ? "0%" : "-10%"}
          backgroundScale={isMobile ? "1.7" : isTablet ? "1.6" : "1.3"}
        />
      </SectionContainer>
      <SectionContainer
        height={isMobile ? "100vh" : isTablet ? "100vh" : "90vh"}>
        <SectionCenteredContent
          headline={pageData.statTitle}
          copy={pageData.statDescription}>
          <ArticleCard articles={pageData.ArticleSet} />
        </SectionCenteredContent>
        <SectionBackground
          background={SectionFiveBackground}
          backgroundHeight={isMobile ? "40%" : isTablet ? "50%" : "150%"}
          backgroundWidth={isMobile ? "100%" : isTablet ? "80%" : "100%"}
          backgroundRight="0%"
          backgroundScale={isMobile ? "3" : isTablet ? "3" : "1.1"}
        />
      </SectionContainer>
      <SectionCallout
        darkShadow
        background={SectionCalloutBackground}
        backgroundSize="cover"
        backgroundPosition="80% 80%"
        title="Start your conversations today."
      />
    </div>
  )
}

export default withScreensize(Home)



// WEBPACK FOOTER //
// ./src/pages/index.js